// @ts-check

/**
 * @file Theme-aware label + text input for filter/search controls that
 * live outside an api-maker Form.
 */

import Text from "@kaspernj/api-maker/build/utils/text"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import {memo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native"

import TextInput from "./text-input"
import useThemeColors, {useThemeName} from "../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @typedef {import("react").RefObject<import("react-native").TextInput | null>} LabeledTextInputRef */
/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} LabeledTextInputProps
 * @property {string} [defaultValue] Initial input value.
 * @property {LabeledTextInputRef} [inputRef] Ref forwarded to the underlying native input.
 * @property {string} [label] Field label rendered above the input.
 * @property {(value: string) => void} [onChangeText] Change handler.
 * @property {string} [placeholder] Placeholder text.
 * @property {string} testID Stable test id.
 * @property {string} [value] Controlled value.
 */

/** @typedef {Record<string, never>} LabeledTextInputState */

/**
 * Labeled text input — uppercase muted label + slot-aware
 * api-maker TextInput with high-contrast border. Use in filter
 * rows where the existing form-bound `FormTextInput` doesn't fit.
 * @augments {ShapeComponent<LabeledTextInputProps, LabeledTextInputState>}
 */
class LabeledTextInput extends ShapeComponent {
  static propTypes = propTypesExact({
    defaultValue: PropTypes.string,
    inputRef: PropTypes.object,
    label: PropTypes.string,
    onChangeText: PropTypes.func,
    placeholder: PropTypes.string,
    testID: PropTypes.string.isRequired,
    value: PropTypes.string
  })
  static defaultProps = {
    defaultValue: undefined,
    inputRef: undefined,
    label: undefined,
    onChangeText: undefined,
    placeholder: undefined,
    value: undefined
  }

  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** Wires the active theme palette into the instance. */
  setup() {
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /**
   * Renders the labeled text input.
   * @returns {ReactNode} Labeled input content.
   */
  render() {
    const {defaultValue, inputRef, label, onChangeText, placeholder, testID, value} = this.p
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    return (
      <View
        style={styles.container ||= {
          flex: 1,
          minWidth: 0,
          width: "100%"
        }}
      >
        {label != null &&
          <Text
            style={styles[`label-${themeName}`] ||= {
              color: colors.textMuted,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 0.5,
              marginBottom: 6,
              textTransform: "uppercase"
            }}
            testID={`${testID}/label`}
          >
            {label}
          </Text>
        }
        <TextInput
          defaultValue={defaultValue}
          inputRef={inputRef}
          onChangeText={onChangeText}
          placeholder={placeholder}
          testID={testID}
          value={value}
        />
      </View>
    )
  }
}

/** @type {NamedExoticComponent<LabeledTextInputProps>} */
const LabeledTextInputComponent = memo(shapeComponent(LabeledTextInput))

export default LabeledTextInputComponent
